// 관리자 주문 관리 API 래퍼.
// 목록은 type(포장/배달)·status 필터를 쿼리스트링으로 전달한다.

import type {
  AdminOrderDto,
  AdminUpdateOrderStatusInput,
  OrderStatus,
  OrderType,
  PrintJobKind,
} from "@oo/shared";
import { api, apiRequest } from "@/api/client.ts";

// 주문 목록. 필터는 모두 선택.
export async function listAdminOrders(filter: {
  type?: OrderType;
  status?: OrderStatus;
}): Promise<AdminOrderDto[]> {
  const params = new URLSearchParams();
  if (filter.type) params.set("type", filter.type);
  if (filter.status) params.set("status", filter.status);
  const qs = params.toString();
  const data = await api.get<{ orders: AdminOrderDto[] }>(`/admin/orders${qs ? `?${qs}` : ""}`);
  return data.orders;
}

// 주문 단건 (주문자 PII 포함).
export async function getAdminOrder(id: string): Promise<AdminOrderDto> {
  const data = await api.get<{ order: AdminOrderDto }>(`/admin/orders/${id}`);
  return data.order;
}

// 상태 변경. rejected 면 rejectReason 필수.
export async function updateOrderStatus(
  id: string,
  input: AdminUpdateOrderStatusInput,
): Promise<AdminOrderDto> {
  // api 헬퍼에 PATCH 단축형은 있으나, 의도를 명확히 하려 apiRequest 직접 호출.
  const data = await apiRequest<{ order: AdminOrderDto }>(`/admin/orders/${id}/status`, {
    method: "PATCH",
    body: input,
  });
  return data.order;
}

// 현장/SMS 주문 수동 결제완료 표시. 인앱(자동청구) 주문은 서버가 409 로 막는다.
export async function markOrderPaid(id: string): Promise<AdminOrderDto> {
  const data = await api.post<{ order: AdminOrderDto }>(`/admin/orders/${id}/payment/mark-paid`);
  return data.order;
}

// 일반 카드결제의 실제 PortOne 상태를 다시 조회해 로컬 주문과 동기화한다.
export async function reconcileOrderPayment(id: string): Promise<AdminOrderDto> {
  const data = await api.post<{ order: AdminOrderDto }>(`/admin/orders/${id}/payment/reconcile`);
  return data.order;
}

// 결제완료 카드 주문 전액 환불(완료 주문 포함). 서버가 PortOne 취소 완료를 확인한 뒤 반환한다.
export async function refundOrderPayment(id: string, reason: string): Promise<AdminOrderDto> {
  const data = await api.post<{ order: AdminOrderDto }>(`/admin/orders/${id}/payment/refund`, {
    reason,
  });
  return data.order;
}

// 영수증 재출력. kind=accept(주방 주문표) | ready(최종 영수증).
//  - 직전 출력 스냅샷을 인쇄 큐에 다시 적재한다. 매장 에이전트가 폴링해 다시 출력한다.
//  - 해당 종류를 한 번도 출력한 적 없으면 서버가 404(NOT_FOUND)로 응답한다.
export async function reprintOrderReceipt(id: string, kind: PrintJobKind): Promise<void> {
  await api.post(`/admin/orders/${id}/reprint`, { kind });
}
